import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import TonightsLineup from "@/components/TonightsLineup";
import SponsorsStrip from "@/components/SponsorsStrip";
import EventBanners from "@/components/EventBanners";
import WhatsAtStake from "@/components/WhatsAtStake";
import PitchSchedule from "@/components/PitchSchedule";
import JudgesGrid from "@/components/JudgesGrid";
import FormsSection from "@/components/FormsSection";
import ReminderBanner from "@/components/ReminderBanner";
import Footer from "@/components/Footer";
import EventbriteEmbed, { EVENTBRITE_URL } from "@/components/EventbriteEmbed";
import SponsorshipSection from "@/components/SponsorshipSection";
import SavePageHero from "@/components/SavePageHero";
import SavePagePopup from "@/components/SavePagePopup";
import FeaturedReplays from "@/components/FeaturedReplays";
import PostPitchExperience from "@/components/PostPitchExperience";
import { getPhase, DEFAULT_SETTINGS } from "@/lib/phase";
import { Button } from "@/components/ui/button";

export default function Home() {
  const [config, setConfig] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [autoOpenForm, setAutoOpenForm] = useState(null);
  const location = useLocation();
  const pitchesRef = useRef(null);
  const formsRef = useRef(null);
  const bannersRef = useRef(null);
  const ticketsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, s, sc, cs] = await Promise.all([
          api.get("/config"),
          api.get("/pitch-schedule"),
          api.get("/site-content"),
          api.get("/campaign-settings"),
        ]);
        setConfig(c.data);
        setSchedule(s.data);
        setSiteContent(sc.data);
        setSettings(cs.data);
      } catch (e) {
        console.error("Load failed", e);
      }
    })();
  }, []);

  // Deep-link handling: /sponsor, /sponsorship, /volunteer, /tickets → auto-scroll (and auto-open form)
  useEffect(() => {
    if (!schedule) return; // wait for page render so refs are populated
    const path = (location.pathname || "").toLowerCase().replace(/\/$/, "");
    const hash = (location.hash || "").toLowerCase();
    const target = path.startsWith("/sponsor") || hash === "#sponsorship" || hash === "#sponsor" ? "sponsorship"
      : path === "/volunteer" || path === "/volunteers" || hash === "#volunteer" ? "volunteer"
      : path === "/tickets" || hash === "#tickets" ? "tickets"
      : null;
    if (!target) return;
    setTimeout(() => {
      if (target === "sponsorship") {
        document.getElementById("sponsorship")?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (target === "volunteer") {
        formsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setAutoOpenForm("volunteer");
      } else if (target === "tickets") {
        ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 400);
  }, [schedule, location.pathname, location.hash]);

  // Broadcast current document height to parent window (for iframe embedding on Wix/etc.)
  useEffect(() => {
    if (window.parent === window) return; // not in an iframe
    let lastHeight = 0;
    const sendHeight = () => {
      const h = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      if (Math.abs(h - lastHeight) < 10) return;
      lastHeight = h;
      window.parent.postMessage({ type: "bs-iframe-resize", height: h }, "*");
    };
    sendHeight();
    const interval = setInterval(sendHeight, 500);
    window.addEventListener("resize", sendHeight);
    return () => { clearInterval(interval); window.removeEventListener("resize", sendHeight); };
  }, []);

  const externalVotingUrl = config?.external_voting_url || "https://www.businessshowers.org/contest";
  const phase = getPhase(settings);
  const visibility = settings?.section_visibility || DEFAULT_SETTINGS.section_visibility;

  const scrollToPitches = () => pitchesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToForms = () => formsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToBanners = () => bannersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToTickets = () => ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleWatchLive = () => {
    const today = new Date().toISOString().slice(0, 10);
    const day = schedule?.days?.find((d) => d.date === today);
    if (day?.zoom_url) window.open(day.zoom_url, "_blank");
    else scrollToPitches();
  };

  // Phase-based section ordering
  const Banners = visibility.banners && siteContent && (
    <div ref={bannersRef} key="banners">
      <EventBanners content={siteContent} onScrollToForms={scrollToForms} onScrollToTickets={scrollToTickets} />
    </div>
  );
  const Tickets = (
    <div ref={ticketsRef} key="tickets">
      <EventbriteEmbed sectionId="tickets" />
    </div>
  );
  const Sponsors = visibility.sponsors && <SponsorsStrip key="sponsors" />;
  const Stakes = visibility.stakes && (
    <WhatsAtStake key="stakes" externalVotingUrl={externalVotingUrl} cohortImage={siteContent?.cohort_image} />
  );
  const Pitches = visibility.pitches && schedule && (
    <div ref={pitchesRef} key="pitches">
      <PitchSchedule
        settings={settings}
        days={schedule.days}
        businesses={schedule.businesses}
        externalVotingUrl={externalVotingUrl}
      />
    </div>
  );
  const Judges = visibility.judges && <JudgesGrid key="judges" judges={schedule?.judges || []} />;
  const Sponsorship = <SponsorshipSection key="sponsorship" sectionId="sponsorship" />;
  const Forms = visibility.forms && (
    <div ref={formsRef} key="forms">
      <FormsSection autoOpenFormType={autoOpenForm} onAutoOpenHandled={() => setAutoOpenForm(null)} />
    </div>
  );

  // Order changes by phase:
  // Phase 1: Pitches, Stakes, Banners, Sponsors, Judges, Sponsorship, Forms
  // Phase 2: Pitches (top), Stakes, Sponsors, Banners, Judges, Sponsorship, Forms
  // Phase 3: Pitches (replays), Stakes, Sponsors, Banners, Sponsorship, Forms
  // Phase 4: Banners (top), Sponsors, Pitches (archive), Judges, Sponsorship, Forms
  // Phase 5: Highlights (TBD), Banners, Sponsors, Pitches (archive), Forms
  const order = (() => {
    if (phase === 1) return [Pitches, Stakes, Banners, Tickets, Sponsors, Judges, Sponsorship, Forms];
    if (phase === 2) return [Pitches, Stakes, Sponsors, Banners, Tickets, Judges, Sponsorship, Forms];
    if (phase === 3) return [Pitches, Stakes, Sponsors, Banners, Tickets, Sponsorship, Forms];
    if (phase === 4) return [Banners, Tickets, Sponsors, Pitches, Stakes, Judges, Sponsorship, Forms];
    return [Banners, Sponsors, Pitches, Sponsorship, Forms];
  })().filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-emerald-950">
      <ReminderBanner externalVotingUrl={externalVotingUrl} settings={settings} />
      <Nav externalVotingUrl={externalVotingUrl} onScrollToForms={scrollToForms} onScrollToPitches={scrollToPitches} />
      <Hero
        settings={settings}
        externalVotingUrl={externalVotingUrl}
        heroImage={siteContent?.hero?.image_url}
        onWatchLive={handleWatchLive}
        onScrollToPitches={scrollToPitches}
        onScrollToForms={scrollToForms}
        onScrollToBanners={scrollToBanners}
      />

      {/* Prominent Save / Install CTA */}
      <SavePageHero />

      {/* Tonight's Lineup — only appears on June 16/17/18 (ET) */}
      <TonightsLineup businesses={schedule?.businesses || []} zoomUrl={schedule?.days?.[0]?.zoom_url} />

      {/* Featured Replays — shows once any pitch has a replay link; prominent placement under hero */}
      {phase >= 2 && phase <= 4 && schedule?.businesses?.some((b) => b.replay_link || b.video_url) && (
        <FeaturedReplays
          businesses={schedule.businesses}
          externalVotingUrl={externalVotingUrl}
        />
      )}

      {/* "You saw the pitches. Now come experience the Business Shower." — Post-pitch campaign section */}
      {phase >= 3 && <PostPitchExperience />}

      {/* Phase 3 voting-push notice with inline replay thumbnails */}
      {phase === 3 && (
        <section data-testid="phase3-block" className="bg-rose-500 text-white py-10 border-b border-rose-700">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-rose-100 font-[Outfit] font-bold">Final Voting Push</div>
              <h2 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl text-white mt-2 leading-tight">
                The pitches are complete. Now the community decides.
              </h2>
              <p className="font-[Outfit] text-rose-50 mt-3">
                Watch the replays, choose the businesses you want to support, and vote by June 22.
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <Button asChild data-testid="phase3-vote" className="rounded-full bg-white text-rose-600 hover:bg-emerald-50 px-6 h-11 font-[Outfit] font-semibold">
                  <a href={externalVotingUrl} target="_blank" rel="noreferrer">Cast Your $2 Vote</a>
                </Button>
                <Button
                  data-testid="phase3-share"
                  variant="outline"
                  onClick={() => {
                    const url = "https://www.businessshowers.org/contest";
                    const text = "Vote in the Freedom In Me Business Showers — $2 moves a business forward.";
                    if (navigator.share) navigator.share({ title: "Business Showers", text, url });
                    else {
                      navigator.clipboard.writeText(`${text} ${url}`);
                      alert("Share link copied to clipboard.");
                    }
                  }}
                  className="rounded-full border-2 border-white bg-transparent text-white hover:bg-white hover:text-rose-600 px-6 h-11 font-[Outfit] font-semibold"
                >
                  Share with a Friend
                </Button>
              </div>
            </div>

            {/* Inline replay thumbnails (3 featured) */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(schedule?.businesses || [])
                  .filter((b) => (b.replay_link || b.video_url) && b.schedule_status !== "Tentative")
                  .slice(0, 3)
                  .map((b) => {
                    const url = b.replay_link || b.video_url;
                    const ytMatch = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
                    const thumb = b.photo_url || (ytMatch ? `https://i.ytimg.com/vi/${ytMatch[1]}/hqdefault.jpg` : "");
                    return (
                      <a
                        key={b.id}
                        href={`/replays#${encodeURIComponent(b.id)}`}
                        data-testid={`phase3-replay-${b.id}`}
                        className="group block rounded-lg overflow-hidden bg-rose-900 border-2 border-rose-300/40 hover:border-amber-300 transition-all hover:-translate-y-0.5"
                      >
                        <div className="aspect-video relative">
                          {thumb && <img src={thumb} alt={b.name} className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-100 transition" />}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-10 w-10 rounded-full bg-white/95 group-hover:bg-amber-300 flex items-center justify-center transition">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-rose-600 ml-0.5"><path d="M8 5v14l11-7z" /></svg>
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-2 bg-white">
                          <div className="font-[Cormorant_Garamond] text-base text-emerald-950 leading-tight truncate">{b.name}</div>
                        </div>
                      </a>
                    );
                  })}
              </div>
              <div className="mt-3 text-right">
                <a
                  href="/replays"
                  data-testid="phase3-all-replays"
                  className="inline-block text-sm font-[Outfit] font-semibold text-amber-200 hover:text-white underline underline-offset-4"
                >
                  See all replays →
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {order}

      <Footer />

      {/* 8-second auto-pop reminder to save the page (suppressed 7 days after dismiss) */}
      <SavePagePopup />
    </div>
  );
}
